import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { SelectArgs, SelectMap } from '@trimble-gcs/i18n-types';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { BaseLayer } from '../base-layer/base-layer.models';
import { MapTool } from '../map.models';
import { mapToolbarTranslations } from './map-toolbar.translations';

interface BaseLayerView extends BaseLayer {
  displayName: string;
}

@Component({
  selector: 'sd-map-toolbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusButtonModule,
    ModusIconModule,
    MatSelectModule,
    ModusTooltipModule,
  ],
  templateUrl: './map-toolbar.component.html',
})
export class MapToolbarComponent {
  private readonly translationService = inject(TranslationService);
  readonly translations = mapToolbarTranslations();

  readonly baseLayers = input.required<BaseLayerView[], BaseLayer[] | null>({
    transform: (value) => {
      return isNil(value) ? [] : value.map((baseLayer) => this.mapToBaseLayerView(baseLayer));
    },
  });

  readonly activeBaseLayer = input.required<BaseLayerView | null, BaseLayer | null>({
    transform: (value) => {
      return isNil(value) ? null : this.mapToBaseLayerView(value);
    },
  });

  readonly activeMapTool = input.required<MapTool>();
  readonly clipAndSaveEnabled = input(false);

  readonly mapToolSelected = output<MapTool>();
  readonly zoomInClick = output();
  readonly zoomOutClick = output();
  readonly fitToViewClick = output();
  readonly baseLayerChange = output<BaseLayer>();

  readonly layerOptionControl = new FormControl<string | null>(null);

  readonly panSelectActive = computed(() => this.activeMapTool() === MapTool.Default);
  readonly rectangleSelectActive = computed(() => this.activeMapTool() === MapTool.RectangleSelect);
  readonly polygonSelectActive = computed(() => this.activeMapTool() === MapTool.PolygonSelect);
  readonly clipRectangleActive = computed(() => this.activeMapTool() === MapTool.ClipRectangle);
  readonly clipPolygonActive = computed(() => this.activeMapTool() === MapTool.ClipPolygon);

  private readonly layerOptionControlValue = toSignal(this.layerOptionControl.valueChanges, {
    initialValue: null,
  });

  constructor() {
    effect(() => {
      const activeBaseLayer = this.activeBaseLayer();
      if (isNil(activeBaseLayer)) return;

      this.layerOptionControl.setValue(activeBaseLayer.name, { emitEvent: false });
    });

    effect(() => {
      const selectedLayerName = this.layerOptionControlValue();
      const selectedLayer = this.baseLayers().find((layer) => layer.name === selectedLayerName);
      if (isNil(selectedLayer)) return;

      this.baseLayerChange.emit(selectedLayer);
    });
  }

  panSelectClick() {
    this.mapToolSelected.emit(MapTool.Default);
  }

  rectangleSelectClick() {
    const mapTool = this.rectangleSelectActive() ? MapTool.Default : MapTool.RectangleSelect;
    this.mapToolSelected.emit(mapTool);
  }

  polygonSelectClick() {
    const mapTool = this.polygonSelectActive() ? MapTool.Default : MapTool.PolygonSelect;
    this.mapToolSelected.emit(mapTool);
  }

  clipRectangleClick() {
    const mapTool = this.clipRectangleActive() ? MapTool.Default : MapTool.ClipRectangle;
    this.mapToolSelected.emit(mapTool);
  }

  clipPolygonClick() {
    const mapTool = this.clipPolygonActive() ? MapTool.Default : MapTool.ClipPolygon;
    this.mapToolSelected.emit(mapTool);
  }

  private mapToBaseLayerView(baseLayer: BaseLayer): BaseLayerView {
    const selectArgs: SelectArgs = { select: baseLayer.name.toLowerCase() };
    const selectMap: SelectMap = this.translations.changeBackground.options;
    const displayName = this.translationService.translateSelect(selectArgs, selectMap);

    return {
      ...baseLayer,
      displayName,
    };
  }
}
